'use client';

import { createContext, useContext, useMemo } from 'react';
import { useRowSelection } from './useRowSelection';

export type BetaDataTableContextValue = {
  /** Select one or more rows by ID. When selectable rows are registered, IDs for non-selectable rows are filtered out. */
  checkRow: (id: string | string[]) => void;
  /** Clear all row selections */
  clearSelection: () => void;
  /** Current context menu coordinates, or null if not open */
  contextMenuCoords: [number, number] | null;
  /** Check whether a row is currently selected */
  isChecked: (id: string) => boolean;
  /** Register a row as selectable (called internally by BetaDataTableRow) */
  registerSelectableRow: (id: string) => void;
  /** Array of row IDs that are currently selectable (have visible checkboxes) */
  selectableRowIds: string[];
  /** Array of currently selected row IDs */
  selectedRows: string[];
  /** Set context menu coordinates */
  setContextMenuCoords: (coords: [number, number] | null) => void;
  /** Directly set the selected rows */
  setSelectedRows: (rows: string[]) => void;
  /** Deselect one or more rows by ID */
  uncheckRow: (id: string | string[]) => void;
  /** Unregister a row as selectable (called internally by BetaDataTableRow) */
  unregisterSelectableRow: (id: string) => void;
};

export const BetaDataTableContext = createContext<BetaDataTableContextValue>({
  checkRow: () => undefined,
  clearSelection: () => undefined,
  contextMenuCoords: null,
  isChecked: () => false,
  registerSelectableRow: () => undefined,
  selectableRowIds: [],
  selectedRows: [],
  setContextMenuCoords: () => undefined,
  setSelectedRows: () => undefined,
  uncheckRow: () => undefined,
  unregisterSelectableRow: () => undefined,
});

/**
 * BetaDataTableProvider must be wrapped around a DataTable component to provide
 * row selection functionality. The provider should be high enough in the tree
 * to be accessible anywhere where table row selection knowledge is needed.
 */
export const BetaDataTableProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    checkRow,
    clearSelection,
    contextMenuCoords,
    isChecked,
    registerSelectableRow,
    selectableRowIds,
    selectedRows,
    setContextMenuCoords,
    setSelectedRows,
    uncheckRow,
    unregisterSelectableRow,
  } = useRowSelection();

  const value = useMemo(
    () => ({
      checkRow,
      clearSelection,
      contextMenuCoords,
      isChecked,
      registerSelectableRow,
      selectableRowIds,
      selectedRows,
      setContextMenuCoords,
      setSelectedRows,
      uncheckRow,
      unregisterSelectableRow,
    }),
    [
      checkRow,
      clearSelection,
      contextMenuCoords,
      isChecked,
      registerSelectableRow,
      selectableRowIds,
      selectedRows,
      setContextMenuCoords,
      setSelectedRows,
      uncheckRow,
      unregisterSelectableRow,
    ]
  );

  return (
    <BetaDataTableContext.Provider value={value}>
      {children}
    </BetaDataTableContext.Provider>
  );
};

export const useBetaDataTableContext = () => {
  return useContext(BetaDataTableContext);
};
